import React, { useState, useEffect, useRef, memo, useMemo } from "react";
import { Bar } from "react-chartjs-2";
import ChartDataLabels from "chartjs-plugin-datalabels";
import Chart from "chart.js/auto";
import "./styles.css";
import { pdf } from "@react-pdf/renderer";
import { API_BASE_URL } from "../config/config";
import { useNavigate } from "react-router-dom";
import { PDFDownloadLink } from "@react-pdf/renderer";
import { Link } from "react-router-dom";
import MyDocument from "./MyDocument"; // PDF component
import InputMask from "react-input-mask";
import { NumericFormat } from "react-number-format";
import {
  calculateHighFinalValues,
  calculateLowFinalValues,
  calculateALowValue,
  calculateBLowValue,
  calculateCLowValue,
  calculateMaxLossrange,
  calculateLossThresold,
  calculateDamageSeverityScore,
  calculateGrade,
  calculatePostRepairFMV,
  calculateInherentDV,
  calculateDiminishedValue,
  calculateMaxLossPercentage,
  calculateDamageToValue,
} from "./utils/formulas"; // Import additional formulas
import { left } from "@cloudinary/url-gen/qualifiers/textAlignment";

Chart.register(ChartDataLabels);


export const companies = {
  ae: { name: "AE", logo: "/ae.svg", logoPng: "/Logo (1).png" },
  dvc: { name: "DVC", logo: "/dvc.svg", logoPng: "/dvc.png" },
  dvg: { name: "DVG", logo: "/dvg.svg", logoPng: "/dvg.png" },
};

const AppraisalReportForm = () => {
  const [formData, setFormData] = useState({
    vehicleOwner: "",
    fileNumber: "",
    dateOfLoss: "",
    dateCreated: "", // 🆕 New Field
    insuranceCo: "",
    claimNumber: "",
    claimType: "First Party Claim",
    vin: "",
    odometerReading: "",
    yearMakeModel: "",
    laborHours: "",
    repairCost: "",
    structuralDamage: "",
    airbagDeployment: "no",
    priorLoss: "no",
    uploadBlackBook: "",
    fmv_high: "",
    a_sign: "+",
    b_sign: "+",
    c_sign: "+",
    a_high: 0,
    b_high: 0,
    c_high: 0,
    final_high: "",
    fmv_low: "",
    a_low: "",
    b_low: "",
    c_low: "",
    a_type: "",
    b_type: "",
    c_type: "",
    final_low: "",
    max_loss: "",
    max_loss_percentage: "", // New field for max loss percentage
    valueDifference: "",
    loss_thresold: "",
    damage_to_value: "",
    PreLossFMV: "",
    inherentDV: "",
    postRepairFMV: "",
    diminishedValue: "",
    damageScore: "",
    grade: "",
    selectedCompany: "ae",
    checkboxes: {
      checkbox1: false,
      checkbox2: false,
      checkbox3: false,
      checkbox4: false,
      checkbox5: false,
      checkbox6: false,
      checkbox7: false,
      checkbox8: false,
      checkbox9: false,
      checkbox10: false,
      checkbox11: false,
      checkbox12: false,
      checkbox13: false,
      checkboxUnder: false,
      checkboxFire: false,
      checkboxWater: false,
      checkboxPdr: false,
      checkboxOther: false,
      yes1: false,
      yes2: false,
      yes3: false,
      yes4: false,
      yes5: false,
      yes6: false,
      checkboxYes: false,
    },
  });

  // THESE ARE THE CHECKBOXES
  const [checkboxes, setCheckboxes] = useState({
    checkbox1: false,
    checkbox2: false,
    checkbox3: false,
    checkbox4: false,
    checkbox5: false,
    checkbox6: false,
    checkbox7: false,
    checkbox8: false,
    checkbox9: false,
    checkbox10: false,
    checkbox11: false,
    checkbox12: false,
    checkbox13: false,
    checkboxUnder: false,
    checkboxFire: false,
    checkboxWater: false,
    checkboxPdr: false,
    checkboxOther: false,
    yes1: false,
    yes2: false,
    yes3: false,
    yes4: false,
    yes5: false,
    yes6: false,
    checkboxYes: false,

    // Add all other checkboxes
  });

  const applySign = (value, sign) => {
    const number = parseFloat(value) || 0;
    return sign === "-" ? -Math.abs(number) : Math.abs(number);
  };

  const clearForm = () => {
    setFormData({
      vehicleOwner: "",
      fileNumber: "",
      dateOfLoss: "",
      insuranceCo: "",
      claimNumber: "",
      claimType: "",
      vin: "",
      odometerReading: "",
      yearMakeModel: "",
      laborHours: "",
      repairCost: "",
      structuralDamage: "",
      airbagDeployment: "",
      priorLoss: "",
      uploadBlackBook: "",
      fmv_high: "",
      a_high: "",
      b_high: "",
      c_high: "",
      final_high: "",
      fmv_low: "",
      a_low: "",
      b_low: "",
      c_low: "",
      final_low: "",
      max_loss: "",
      valueDifference: "",
      loss_thresold: "",
      damage_to_value: "",
      PreLossFMV: "",
      inherentDV: "",
      postRepairFMV: "",
      diminishedValue: "",
      damageScore: "",
      grade: "",
    });
    setIsPDFReady(false);
  };

  const isValidChartImage = (image) => {
    return image && image.startsWith("data:image/png;base64,");
  };

  // Function to calculate the damage to value ratio
  const calculateDamageToValue = (repairCost, finalHighValue) => {
    if (finalHighValue > 0) {
      return ((repairCost / finalHighValue) * 100).toFixed(0); // Return the percentage as a number
    }
    return ""; // Return empty if finalHighValue is zero or undefined
  };

  const chartData = {
    labels: ["Damage Score", "Diminished Value"],
    datasets: [
      {
        label: "Values",
        data: [formData.damageScore || 0, formData.diminishedValue || 0], // Ensure numeric values
        backgroundColor: ["#FF6384", "#36A2EB"],
      },
    ],
  };

  const ChartComponent = ({ finalHighValue = 0, postRepairFMV = 0, diminishedValue = 0, setChartImage, textColor = "#000000" }) => {
    const chartRef = useRef(null);
    const svgImagesRef = useRef([]); // Store loaded images

    // 🔹 Load Arrow Images Once
    useEffect(() => {
      const svgUrls = ["/greenArrow.png", "/blueArrow.png", "/orangeArrow.png"];

      let imagesLoadedCount = 0;
      svgUrls.forEach((url, index) => {
        const img = new Image();
        img.src = url;
        img.onload = () => {
          svgImagesRef.current[index] = img;
          imagesLoadedCount++;

          if (imagesLoadedCount === svgUrls.length) {
            // 🔥 Force chart update so arrows appear
            if (chartRef.current) {
              chartRef.current.update();
            }
          }
        };

        img.onerror = () => console.error(`❌ Failed to load image: ${url}`);
      });
    }, []);

    // 🔥 Arrow Plugin (Fixes Position Tracking)
    const arrowPlugin = {
      id: "arrowPlugin",
      afterDatasetsDraw: (chart) => {
        const ctx = chart.ctx;
        ctx.save();

        chart.data.datasets.forEach((_, datasetIndex) => {
          const meta = chart.getDatasetMeta(datasetIndex);
          meta.data.forEach((bar, index) => {
            if (!bar) return;

            // ✅ Get bar's tooltip position (most accurate method)
            const { x: barX, y: barY } = bar.tooltipPosition();

            if (svgImagesRef.current[index]) {
              const img = svgImagesRef.current[index];

              // ✅ Glue arrow **under** the label container
              const arrowWidth = 40;
              const arrowHeight = 20;
              const arrowX = barX - arrowWidth / 2;
              const arrowY = barY - 40; // 🔥 Adjust dynamically so arrow follows

              ctx.drawImage(img, arrowX, arrowY, arrowWidth, arrowHeight);
            } else {
            }
          });
        });

        ctx.restore();
      },
    };

    useEffect(() => {
      if (chartRef.current && chartRef.current.toBase64Image) {
        console.log("🔄 Waiting for chart update...");

        setTimeout(() => {
          if (chartRef.current) {
            const base64Image = chartRef.current.toBase64Image();

            if (isValidChartImage(base64Image)) {
              setChartImage(base64Image);
            } else {
            }
          } else {
          }
        }, 200); // 🔥 Small delay ensures chart is fully rendered
      } else {
      }
    }, [finalHighValue, postRepairFMV, diminishedValue]);

    const data = {
      labels: ["Final High Value", "Post Repair FMV", "Total Diminished Value"],
      datasets: [
        {
          data: [Math.round(finalHighValue), Math.round(postRepairFMV), Math.round(diminishedValue)],
          backgroundColor: ["#62deaa", "#52a0ba", "#ffb578"],
          borderWidth: 0,
          borderRadius: 12,
          barPercentage: 0.7,
          categoryPercentage: 0.8,
        },
      ],
    };

    const maxValue = Math.max(finalHighValue, postRepairFMV, diminishedValue);
    const topPadding = maxValue > 100000 ? 80 : 50;

    const options = {
      responsive: true,
      maintainAspectRatio: true,
      layout: {
        padding: {
          top: topPadding,
          bottom: 35,
        },
      },
      plugins: {
        legend: { display: false },
        datalabels: {
          display: true,
          color: "#ffffff",
          borderRadius: 17,
          padding: { top: 18, bottom: 18, left: 50, right: 50 },
          minWidth: 200,
          maxWidth: 200,
          minHeight: 56,
          textAlign: "center",
          anchor: "end",
          align: "top",
          offset: 40,
          clip: false,
          font: {
            weight: "bold",
            size: 20,
            lineHeight: 1,
            family: "'Gilroy', sans-serif",
          },
          backgroundColor: (context) => {
            const colors = ["#00c274", "#006080", "#fa9943"];
            return colors[context.dataIndex];
          },
          formatter: (value) => {
            let formatted = `$${Math.round(value).toLocaleString("en-US")}`;
            const TARGET_CHARS = 12;
            const clean = formatted.replace(/,/g, "");

            if (clean.length > TARGET_CHARS) {
              return `${clean.slice(0, TARGET_CHARS - 3)}...`.padEnd(TARGET_CHARS, "\u00A0");
            }

            const totalSpaces = TARGET_CHARS - clean.length;
            const leftSpaces = Math.floor(totalSpaces / 2);
            const rightSpaces = totalSpaces - leftSpaces;

            return "\u00A0".repeat(leftSpaces) + formatted + "\u00A0".repeat(rightSpaces);
          },
        },
        tooltip: {
          enabled: true,
          backgroundColor: "rgba(0, 0, 0, 0.7)",
          titleFont: { size: 14 },
          bodyFont: { size: 12 },
          callbacks: {
            label: (context) => `$${Math.round(context.raw).toLocaleString("en-US")}`,
          },
        },
      },
      scales: {
        y: {
          beginAtZero: true,
          grid: { display: false },
          suggestedMax: maxValue * 1.2,
        },
        x: {
          grid: { display: false },
          ticks: {
            color: "#555",
            font: { size: 16 },
          },
        },
      },
      animation: { duration: 0 },
    };

    return <Bar ref={chartRef} data={data} options={options} plugins={[ChartDataLabels, arrowPlugin]} />;
  };

  console.log("Form Data in handleSubmit:", formData); // Check if dateOfLoss has a value

  // Function to generate and upload the PDF, returning the accessible URL
  const pdfGenerationFunction = async (formData, selectedCompanyLogo) => {
    try {
      const blob = await pdf(
        <MyDocument
          formData={formData}
          chartImage={chartImage}
          companyLogo={companies[formData.selectedCompany]?.logoPng} // Dynamically pass the PN
        />
      ).toBlob();

      const pdfUrl = await uploadPdfToStorage(blob, `${formData.vin}.pdf`);
      return pdfUrl;
    } catch (error) {
      console.error("Error generating or uploading PDF:", error);
      throw error;
    }
  };

  const fetchVINData = async (vin) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/vin/${vin}`);
      if (!response.ok) {
        throw new Error("Failed to fetch VIN data");
      }

      const data = await response.json();
      return data; // Return the VIN data
    } catch (error) {
      console.error("Error fetching VIN data:", error);
      return null; // Return null if there's an error
    }
  };

  const fetchMarketValue = async (vin) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/marketvalue/${vin}`);
      if (!response.ok) {
        throw new Error("Failed to fetch market value");
      }

      const data = await response.json();
      return data; // Return market value data
    } catch (error) {
      console.error("Error fetching market value:", error);
      return null;
    }
  };

  const uploadPdfToStorage = async (blob, fileName) => {
    const uploadData = new FormData(); // Rename to `uploadData` to avoid conflict
    uploadData.append("pdfFile", blob, fileName); // Attach PDF Blob as 'pdfFile'
    uploadData.append("formData", JSON.stringify(formData)); // Attach form data as JSON

    const response = await fetch(`${API_BASE_URL}/api/forms`, {
      method: "POST",
      body: uploadData,
    });

    if (!response.ok) throw new Error("PDF upload failed");
    const { pdfUrl } = await response.json();
    return pdfUrl;
  };

  const closePreview = () => {
    setShowPreview(false); // Fecha a pré-visualização
  };

  const [chartImage, setChartImage] = useState(""); // State to store chart image as base64
  const [isPDFReady, setIsPDFReady] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false); // Estado para o menu suspenso
  const chartRef = useRef(null); // Reference for the chart
  const [pdfBlob, setPdfBlob] = useState(null);
  const [showPreview, setShowPreview] = useState(false); // Control the preview visibility
  const [previewImageSrc, setPreviewImageSrc] = useState("/previewButtonHeader.svg");
  const [savedFormsImageSrc, setSavedFormsImageSrc] = useState("/savedFormsButtomHeader.svg");
  const [fileTag, setFileTag] = useState(""); // State to store the file name
  const [isDragging, setIsDragging] = useState(false); // State to handle dragging effect
  const [saveButtonImageSrc, setSaveButtonImageSrc] = useState("/saveButtonHeader.svg");
  const [errorMessage, setErrorMessage] = useState("");
  const [selectedCompany, setSelectedCompany] = useState("ae"); // Default company
  const navigate = useNavigate();
  const [isFormComplete, setIsFormComplete] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const companyLogo = formData?.companyLogo || "/Logo (1).png"; // Ensure it's defined
  console.log("Preview Company Logo:", companyLogo);

  //HANDLE FUNCTIONS ARE HERE ↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓

  const handleDamageScoreChange = (value) => {
    // Ensure the value is a number or defaults to 0
    const parsedValue = parseFloat(value) || 0;

    setFormData((prevFormData) => {
      const updatedFormData = { ...prevFormData, damageScore: parsedValue };

      console.log("Updated FormData in handleDamageScoreChange:", updatedFormData);

      // Only recalculate other values if they depend on factors other than damageScore
      const inherentDV = calculateInherentDV(updatedFormData.max_loss, parsedValue);
      const postRepairFMV = calculatePostRepairFMV(updatedFormData.PreLossFMV, inherentDV);
      const diminishedValue = calculateDiminishedValue(updatedFormData.final_high, postRepairFMV);

      // Calculate the grade using your provided logic
      const grade = calculateGrade(parsedValue);

      return {
        ...updatedFormData,
        inherentDV: inherentDV.toFixed(0),
        postRepairFMV: postRepairFMV.toFixed(0),
        diminishedValue: diminishedValue.toFixed(0),
        grade, // Update the grade based on damage score
      };
    });
  };

  const handleRadioChange = (e) => {
    const { name, value } = e.target;

    setFormData((prevFormData) => {
      let updatedFormData = { ...prevFormData, [name]: value };

      // Handle structuralDamage assignment if this is a yes1/yes2 radio group

      // Recalculate damage-related values
      const structural = updatedFormData.structuralDamage && updatedFormData.structuralDamage !== "None";
      const airbag = updatedFormData.airbagDeployment === "yes";
      const laborHours = parseFloat(updatedFormData.laborHours) || 0;
      const repairCost = parseFloat(updatedFormData.repairCost) || 0;
      const damageToValue = calculateDamageToValue(repairCost, updatedFormData.final_high);

      const baseDamageScore = calculateDamageSeverityScore(laborHours, repairCost, damageToValue / 100, structural, airbag);

      return {
        ...updatedFormData,
        damage_to_value: damageToValue,
      };
    });
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;

    // Update the checkboxes state
    setCheckboxes((prevCheckboxes) => ({
      ...prevCheckboxes,
      [name]: checked,
    }));

    // Update the formData state and handle specific cases for structuralDamage and airbagDeployment
    setFormData((prevFormData) => {
      let updatedFormData = { ...prevFormData, [name]: checked };

      // Handle specific checkbox groups logic
      if (name === "yes1" || name === "yes2") {
        updatedFormData = {
          ...updatedFormData,
          structuralDamage: name === "yes1" ? "yes" : "no",
        };
      }

      if (name === "yes3" || name === "yes4") {
        updatedFormData = {
          ...updatedFormData,
          airbagDeployment: name === "yes3" ? "yes" : "no",
        };
      }

      return updatedFormData;
    });
  };

  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    let adjustedAHigh, adjustedBHigh, adjustedCHigh;
    let a_low, b_low, c_low;
    let highFinalValuesResult, lowFinalValuesResult;
    let preLossFMV, maxLossRange, maxLossPercentage;
    let lossThreshold, damageToValue;
    let structural, airbag, laborHours, repairCost;
    let calculatedDamageScore, inherentDV, postRepairFMV;
    let finalHighValue, diminishedValue, calculatedGrade;
    // If the field is a checkbox, handle its state
    if (type === "checkbox") {
      setCheckboxes((prevCheckboxes) => {
        const updatedCheckboxes = { ...prevCheckboxes };

        // Toggle the checkbox state
        updatedCheckboxes[name] = !prevCheckboxes[name];

        // Handle checkbox-specific logic
        if (updatedCheckboxes[name]) {
          if (name === "yes1" || name === "yes2") {
            updatedCheckboxes.yes1 = name === "yes1";
            updatedCheckboxes.yes2 = name === "yes2";
            setFormData((prevFormData) => ({
              ...prevFormData,
              structuralDamage: name === "yes1" ? "yes" : "no",
            }));
          }
          if (name === "yes3" || name === "yes4") {
            updatedCheckboxes.yes3 = name === "yes3";
            updatedCheckboxes.yes4 = name === "yes4";
            setFormData((prevFormData) => ({
              ...prevFormData,
              airbagDeployment: name === "yes3" ? "yes" : "no",
            }));
          }
          if (name === "yes5" || name === "yes6") {
            updatedCheckboxes.yes5 = name === "yes5";
            updatedCheckboxes.yes6 = name === "yes6";
            setFormData((prevFormData) => ({
              ...prevFormData,
              priorLoss: name === "yes5" ? "yes" : "no",
            }));
          }
        }

        return updatedCheckboxes;
      });
    } else {
      setFormData((prevFormData) => {
        const isNumericField = ["fmv_high", "fmv_low", "a_high", "a_low", "b_high", "b_low", "c_high", "c_low", "repairCost", "laborHours"].includes(name);

        // Ensure two decimal places for laborHours and correct handling for other numeric fields
        let updatedValue = value;

        if (isNumericField) {
          // Ensure value is treated as string for .replace() to work safely
          updatedValue = String(value).replace(/[^0-9.]/g, "");

          if (updatedValue.includes(".")) {
            const [integer, decimals] = updatedValue.split(".");
            updatedValue = integer + (decimals ? `.${decimals.slice(0, 2)}` : ".");
          }

          if (["a_high", "b_high", "c_high"].includes(name)) {
            updatedValue = Math.abs(parseFloat(updatedValue)) || 0;
          }
        }

        // Handle empty values
        if (updatedValue === "") {
          updatedValue = "";
        }

        const updatedFormData = {
          ...prevFormData,
          [name]: updatedValue,
        };

        const adjustedAHigh = applySign(updatedFormData.a_high, updatedFormData.a_sign);
        const adjustedBHigh = applySign(updatedFormData.b_high, updatedFormData.b_sign);
        const adjustedCHigh = applySign(updatedFormData.c_high, updatedFormData.c_sign);

        const a_low = calculateALowValue(updatedFormData.fmv_high, adjustedAHigh, updatedFormData.fmv_low);

        const adjustedBLow = applySign(parseFloat(updatedFormData.b_low) || 0, updatedFormData.b_sign);
        const adjustedCLow = applySign(parseFloat(updatedFormData.c_low) || 0, updatedFormData.c_sign);

        const b_low = adjustedBLow;
        const c_low = adjustedCLow;

        const highFinalValuesResult = calculateHighFinalValues(updatedFormData.fmv_high, adjustedAHigh, adjustedBHigh, adjustedCHigh);

        const lowFinalValuesResult = calculateLowFinalValues(updatedFormData.fmv_low, a_low, adjustedBLow, adjustedCLow);

        const preLossFMV = highFinalValuesResult;

        const maxLossRange = calculateMaxLossrange(updatedFormData.fmv_high, adjustedAHigh, adjustedBHigh, adjustedCHigh, updatedFormData.fmv_low, a_low, b_low, c_low);

        const maxLossPercentage = calculateMaxLossPercentage(maxLossRange, highFinalValuesResult) * 100;

        const lossThreshold = calculateLossThresold(updatedFormData.fmv_high, adjustedAHigh, adjustedBHigh, adjustedCHigh);

        const damageToValue = calculateDamageToValue(updatedFormData.repairCost, highFinalValuesResult);

        const structural = updatedFormData.structuralDamage && updatedFormData.structuralDamage !== "None";
        const airbag = updatedFormData.airbagDeployment === "yes";
        const laborHours = parseFloat(updatedFormData.laborHours) || 0;
        const repairCost = parseFloat(updatedFormData.repairCost) || 0;

        const calculatedDamageScore = calculateDamageSeverityScore(laborHours, repairCost, damageToValue / 100, structural, airbag);

        const adjustedDamageScore = structural ? calculatedDamageScore : calculatedDamageScore;
        console.log("🛠 structuralDamage:", updatedFormData.structuralDamage);
        console.log("⚠️ structural flag:", structural);
        console.log("📊 Damage Score (adjusted):", adjustedDamageScore);

        const inherentDV = calculateInherentDV(maxLossRange, adjustedDamageScore);
        const postRepairFMV = calculatePostRepairFMV(preLossFMV, inherentDV);
        const finalHighValue = parseFloat(highFinalValuesResult) || 0;
        const diminishedValue = calculateDiminishedValue(finalHighValue, postRepairFMV);
        const calculatedGrade = calculateGrade(calculatedDamageScore);

        // Log current form data for debugging
        console.log("Current Form Data:", updatedFormData);

        // Add VIN-specific logic
        if (name === "vin" && value.length === 17) {
          fetchVINData(value)
            .then((vinData) => {
              if (vinData) {
                const year = vinData.Results.find((item) => item.Variable === "Model Year")?.Value || "";
                const make = vinData.Results.find((item) => item.Variable === "Make")?.Value || "";
                const model = vinData.Results.find((item) => item.Variable === "Model")?.Value || "";

                setFormData((prevFormData) => ({
                  ...prevFormData,
                  yearMakeModel: `${year} ${make} ${model}`.trim(),
                }));
              }
            })
            .catch((error) => console.error("Error fetching VIN data:", error));

          fetchMarketValue(value)
            .then((marketValue) => {
              console.log("Market Value Response:", marketValue); // Log the full API response for debugging

              if (marketValue) {
                const high = marketValue.above; // Map `above` to `fmv_high`
                const low = marketValue.below; // Map `below` to `fmv_low`

                setFormData((prevFormData) => ({
                  ...prevFormData,
                  fmv_high: high || "", // Update high value
                  fmv_low: low || "", // Update low value
                }));
                console.log("FMV High:", high); // Log high value
                console.log("FMV Low:", low); // Log low value
              }
            })
            .catch((error) => console.error("Error fetching market value data:", error));
        }
        console.log("🔥 handleInputChange called — field:", e.target.name, "value:", e.target.value);
        console.log("🧠 FINAL adjustedDamageScore being saved:", adjustedDamageScore);
        return {
          ...updatedFormData,
          a_low,
          b_low,
          c_low,
          final_high: highFinalValuesResult,
          PreLossFMV: preLossFMV,
          final_low: lowFinalValuesResult,
          max_loss: maxLossRange,
          max_loss_percentage: maxLossPercentage.toFixed(0),
          valueDifference: highFinalValuesResult - lowFinalValuesResult,
          loss_thresold: lossThreshold,
          damage_to_value: damageToValue,
          damageScore: adjustedDamageScore.toFixed(0),
          grade: calculateGrade(adjustedDamageScore),
          inherentDV: inherentDV.toFixed(0),
          postRepairFMV: postRepairFMV.toFixed(0),
          diminishedValue: diminishedValue.toFixed(0),
        };
      });
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleFileUpload(file); // Validate and upload the file
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    // Update form data dynamically
    setFormData((prevFormData) => ({
      ...prevFormData,
      [name]: value, // Set the field dynamically
    }));
  };

  // Function to handle file upload
  const validImageTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"]; // Allowed image MIME types

  const handleFileUpload = async (file) => {
    try {
      // Validate the file type before uploading
      if (!validImageTypes.includes(file.type)) {
        setErrorMessage("Invalid file format, only images can be uploaded"); // Display error
        setFileTag(""); // Clear any existing file tag
        return; // Exit early if the file is not valid
      }

      const formData = new FormData();
      formData.append("image", file); // 'image' matches your backend key

      // Upload the file to the backend
      const response = await fetch(`${API_BASE_URL}/api/upload-image`, {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        throw new Error("Failed to upload image");
      }

      const data = await response.json();
      const imageUrl = data.imageUrl; // The URL of the uploaded image

      // Update the formData state with the image URL
      setFormData((prevFormData) => ({
        ...prevFormData,
        uploadBlackBook: imageUrl, // Save the image URL to the formData object
      }));

      setFileTag(file.name); // Store the file name for UI display
      setErrorMessage(""); // Clear any error message
      console.log("Image uploaded successfully:", imageUrl);
    } catch (error) {
      console.error("Error uploading image:", error);
      setErrorMessage("Failed to upload image. Please try again."); // Display error
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileUpload(file); // Validate and upload the file
    }
  };

  const handleCompanyChange = (event) => {
    const selected = event.target.value;
    console.log("Selected company:", selected); // Log the selected company
    console.log("Selected Company Logo:", companies[selectedCompany]?.logo);
    setSelectedCompany(selected); // Update the state
  };

  // Use it in the preview function:
  const handlePreview = async () => {
    setIsLoading(true);

    // Fetch the latest company logo dynamically
    const latestSelectedCompany = formData.selectedCompany; // Ensure current selection
    const updatedCompanyLogo = companies[latestSelectedCompany]?.logoPng;

    console.log("Current Selected Company:", latestSelectedCompany);
    console.log("Company Logo for Preview:", updatedCompanyLogo);

    // Generate the PDF with the dynamically updated logo
    const blob = await pdf(
      <MyDocument
        formData={formData} // Current form data
        chartImage={chartImage}
        companyLogo={updatedCompanyLogo} // Pass updated logo dynamically
      />
    ).toBlob();

    setPdfBlob(blob);
    setShowPreview(true);
    setIsLoading(false);

    console.log("PDF Preview Generated with Logo:", updatedCompanyLogo);
  };

  const handleGenerateAndDownloadPDF = async () => {
    setIsLoading(true);

    try {
      console.log("📌 Generating PDF...");
      console.log("📌 Current Chart Image:", chartImage?.substring(0, 50)); // Show first 50 chars

      if (!isValidChartImage(chartImage)) {
        console.error("❌ Error: Chart image is invalid or not ready!");
        setIsLoading(false);
        return;
      }

      console.log("✅ Chart Image is Valid, Proceeding with PDF Generation...");

      const blob = await pdf(<MyDocument formData={formData} chartImage={chartImage} companyLogo={companies[selectedCompany]?.logoPng} />).toBlob();

      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${formData.vin || "AppraisalReport"}_${formData.dateOfLoss || "NoDate"}_${selectedCompany}.pdf`;
      link.click();

      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("❌ Error generating the PDF:", error);
    } finally {
      setIsLoading(false);
    }
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
  
    const getCurrentDate = () => {
      return new Date().toISOString(); 
    };
  
    const currentDate = getCurrentDate();
  
    const updatedFormData = {
      ...formData,
      dateCreated: currentDate,
    };
  
    const { vin, dateOfLoss } = updatedFormData;
  
    try {
      // ✅ 1. DUPLICATE CHECK BEFORE SUBMITTING
      const checkResponse = await fetch(`${API_BASE_URL}/api/forms/check-duplicate?vin=${encodeURIComponent(vin)}&dateOfLoss=${encodeURIComponent(dateOfLoss)}`);
      if (!checkResponse.ok) throw new Error("Failed to check for duplicates");
      const checkData = await checkResponse.json();
  
      if (checkData.exists) {
        alert("❌ A form with this VIN and Date of Loss already exists.");
        return; // 🚫 Stop here
      }
  
      // 🧠 Generate PDF
      const pdfBlob = await pdfGenerationFunction(updatedFormData);
      if (!pdfBlob) {
        alert("Form saved successfully!");
        return;
      }
  
      const formDataToSend = new FormData();
      formDataToSend.append("pdfFile", pdfBlob, `${vin}_${dateOfLoss}.pdf`);
      formDataToSend.append("formData", JSON.stringify(updatedFormData));
  
      const response = await fetch(`${API_BASE_URL}/api/forms`, {
        method: "POST",
        body: formDataToSend,
      });
  
      if (response.ok) {
        alert("Form saved successfully!");
        const jsonResponse = await response.json();
        console.log("✅ Server response:", jsonResponse);
      } else {
        const errorResponse = await response.json();
        console.error("❌ Error:", errorResponse);
        alert("Error saving form.");
      }
    } catch (error) {
      console.error("💥 Submission error:", error);
      alert("An error occurred while saving the form.");
    }
  };
  
  

  //HANDLE FUNCTIONS ARE HERE ↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑

  //USE EFFECTS ARE HERE ↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓

  // Check if vin and dateOfLoss are filled
  useEffect(() => {
    if (formData.vin && formData.dateOfLoss) {
      setIsFormComplete(true);
    } else {
      setIsFormComplete(false);
    }
  }, [formData.vin, formData.dateOfLoss]);

  useEffect(() => {
    console.log("🟢 Page loaded: Initial structuralDamage value =", formData.structuralDamage);
  }, []);

  useEffect(
    () => {
      setFormData((prevFormData) => {
        if (!prevFormData.isDamageScoreEdited) {
          // Only calculate damageScore if not manually edited
          const calculatedDamageScore = calculateDamageSeverityScore(/* inputs */);
          const calculatedGrade = calculateGrade(calculatedDamageScore);

          return {
            ...prevFormData,
            damageScore: calculatedDamageScore,
            grade: calculatedGrade, // Update dependent values
          };
        }

        // If edited, keep the user-provided value
        return prevFormData;
      });
    },
    [
      /* dependencies like laborHours, repairCost, etc. */
    ]
  );

  const pdfUrl = useMemo(() => {
    if (!pdfBlob) return null;

    const url = URL.createObjectURL(pdfBlob);
    return url;
  }, [pdfBlob]);

  useEffect(() => {
    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [pdfUrl]);

  useEffect(() => {
    const checkImageUrl = async () => {
      try {
        const response = await fetch(formData.uploadBlackBook, {
          method: "HEAD",
        });
        if (response.ok) {
          console.log("Image URL is valid:", formData.uploadBlackBook);
        } else {
          console.error("Image URL is not accessible:", formData.uploadBlackBook);
        }
      } catch (error) {
        console.error("Error validating image URL:", error);
      }
    };

    if (formData.uploadBlackBook) checkImageUrl();
  }, [formData.uploadBlackBook]);

  useEffect(() => {
    console.log("Updated Company Logo:", companyLogo);
  }, [companyLogo]);

  useEffect(() => {
    if (showPreview) {
      handlePreview();
    }
  }, [formData.selectedCompany]); // Trigger when selectedCompany changes

  useEffect(() => {
    const rawA = parseFloat(formData.a_low) || 0;
    const rawB = parseFloat(formData.b_low) || 0;
    const rawC = parseFloat(formData.c_low) || 0;

    const signB = formData.b_sign || "+";
    const signC = formData.c_sign || "+";

    const adjustedA = rawA;
    const adjustedB = applySign(rawB, signB);
    const adjustedC = applySign(rawC, signC);

    const fmvLow = parseFloat(formData.fmv_low) || 0;

    const finalLow = calculateLowFinalValues(fmvLow, adjustedA, adjustedB, adjustedC);
    const finalHigh = parseFloat(formData.final_high) || 0;
    const valueDiff = finalHigh - finalLow;

    console.log("🔍 useEffect CALLED for LOW VALUES");
    console.log("🅰️ A Low:", rawA);
    console.log("🅱️ B Low Raw:", rawB, " | Sign:", signB, " | Adjusted:", adjustedB);
    console.log("🌊 C Low Raw:", rawC, " | Sign:", signC, " | Adjusted:", adjustedC);
    console.log("📉 FMV Low:", fmvLow);
    console.log("✅ Calculated Final Low:", finalLow);
    console.log("🔻 Value Difference (High - Low):", valueDiff);

    setFormData((prev) => ({
      ...prev,
      final_low: finalLow,
      valueDifference: valueDiff,
    }));
  }, [formData.a_low, formData.b_low, formData.b_sign, formData.c_low, formData.c_sign, formData.fmv_low, formData.final_high]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  //USE EFFECTS ARE HERE ↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑

  const MemoizedChartComponent = React.memo(ChartComponent);

  const memoizedFormData = useMemo(
    () => ({
      finalHighValue: parseFloat(formData.final_high) || 0,
      postRepairFMV: parseFloat(formData.postRepairFMV) || 0,
      diminishedValue: parseFloat(formData.diminishedValue) || 0,
    }),
    [formData.final_high, formData.postRepairFMV, formData.diminishedValue]
  );

  console.log("Uploaded Image URL:", formData.uploadBlackBook);

  return (
    <div
      style={{
        background: "linear-gradient(237deg, rgba(39, 39, 39, 0) 70%, rgba(0, 195, 116, 0.4) 120%), #272727",
        backgroundAttachment: "fixed", // This fixes the background in place
        backgroundSize: "cover", // Ensure the background covers the whole page
      }}
    >
      <header>
        <div className="header-container">
          {/* Dynamic SVG Logo */}
          <img
            src={companies[selectedCompany].logo} // SVG for the form
            alt="Logo"
            className="header-image"
          />

          {/* Dropdown */}
          <select
            className="company-dropdown"
            value={selectedCompany} // Sync with selectedCompany state
            onChange={(e) => {
              const updatedCompany = e.target.value;
              console.log("Selected Company Changed To:", updatedCompany);

              // Update both selectedCompany and formData
              setSelectedCompany(updatedCompany);
              setFormData((prevFormData) => ({
                ...prevFormData,
                selectedCompany: updatedCompany,
              }));
            }}
            style={{ padding: "8px", fontSize: "16px" }}
          >
            {Object.entries(companies).map(([key, { name }]) => (
              <option key={key} value={key}>
                {name}
              </option>
            ))}
          </select>
        </div>

        <div className="actions">
          <button className="export" onClick={handleGenerateAndDownloadPDF} disabled={isLoading}>
            <img className="download-icon" src="/image 4.png" alt="Export" />
            {isLoading ? "Export" : "Export"}
          </button>

          <button className="clear" onClick={() => setFormData({})}>
            Clear
          </button>

          <button
            className="google-button"
            onClick={handleSubmit}
            type="submit" // Change to submit
            disabled={!isFormComplete} // Disable the button if form is not complete
          >
            <div className="save-icon" role="img" aria-label="Preview icon" />
          </button>

          <button
            className="otherButtonsPreview"
            onClick={() => {
              console.log("Before Preview Clicked. Current Logo:", companyLogo);
              handlePreview();
            }}
          >
            <div className="preview-icon" role="img" aria-label="Preview icon" />
          </button>

          <button
            className="otherButtons"
            onClick={() => {
              window.scrollTo(0, 0);
              navigate("/forms");
            }}
          >
            <div className="saved-icon" role="img" aria-label="Preview icon" />
          </button>
          <div className="dropdownWrapperDvq" ref={dropdownRef}>
            <img src="/threePointsCircle.svg" alt="Menu" className="dropdownTriggerDvq" onClick={() => setDropdownOpen(!dropdownOpen)} />

            {dropdownOpen && (
              <div className="dropdownMenuDvq">
                <div
                  className="dropdownItemDvq"
                  onClick={() => {
                    window.scrollTo(0, 0);
                    navigate("/search-certificates");
                    setDropdownOpen(false);
                  }}
                >
                  <span className="spanTextDropdownHeader">Appraisal Request</span>
                </div>
                <div
                  className="dropdownItemDvq"
                  onClick={() => {
                    window.scrollTo(0, 0);
                    navigate("/dv-quote");
                    setDropdownOpen(false);
                  }}
                >
                  <span className="spanTextDropdownHeader">DV Quote</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </header>

      <div className="container">
        <div className="titleDiv">
          <h1 className="bigTitle">Appraisal Report</h1>
          <p className="subTitle">Fill out the form to create a professional Appraisal Report.</p>
        </div>
        <section className="form-section">
          <h2>Claim Information</h2>
          <form>
            <div className="form-group">
              <label className="titleForInformation" htmlFor="vehicleOwner">
                Vehicle Owner
              </label>
              <input className="vehicleOwner" type="text" name="vehicleOwner" value={formData.vehicleOwner} onChange={handleChange} placeholder="Full Name" />
            </div>
            <div className="form-group">
              <label className="titleForInformation" htmlFor="fileNumber">
                File Number
              </label>
              <input className="fileNumber" type="text" name="fileNumber" value={formData.fileNumber} onChange={handleChange} placeholder="#AAAA" />
            </div>
            <div className="form-group">
              <label className="titleForInformation" htmlFor="dateOfLoss">
                Date of Loss
              </label>

              <InputMask
                mask="99/99/9999"
                maskChar={null} // Remove os caracteres de máscara extras
                value={formData.dateOfLoss}
                onFocus={(e) => {
                  // Força o campo a ficar vazio ao clicar pela primeira vez
                  if (!formData.dateOfLoss) {
                    e.target.value = "";
                  }
                }}
                onChange={(e) => {
                  const { value } = e.target;
                  setFormData((prevFormData) => ({
                    ...prevFormData,
                    dateOfLoss: value,
                  }));
                }}
                placeholder="MM/DD/YYYY"
                className="dateOfLoss" // Mantendo a mesma classe
              />
            </div>
            <div className="form-group">
              <label className="titleForInformation" htmlFor="insuranceCo">
                Insurance Co:
              </label>
              <input className="insuranceCo" name="insuranceCo" value={formData.insuranceCo} onChange={handleChange} placeholder="Company" />
            </div>
            <div className="form-group">
              <label className="titleForInformation" htmlFor="claimNumber">
                Claim Number & Type
              </label>
              <input className="claimNumber" type="text" name="claimNumber" value={formData.claimNumber} onChange={handleChange} placeholder="99-999999999-99" />
              <select
                className="type"
                name="claimType"
                value={formData.claimType || "First Party Claim"} // Default to First Party Claim
                onChange={handleChange}
              >
                <option className="dropddownOptions" value="First Party Claim">
                  First Party Claim
                </option>
                <option className="dropddownOptions" value="Third Party Claim">
                  Third Party Claim
                </option>
              </select>
            </div>
          </form>
        </section>

        <section className="form-section">
          <form>
            <h2 className="section-heading">Vehicle Information</h2>
            <div className="form-group">
              <label htmlFor="vin">VIN</label>
              <input className="vin" type="text" name="vin" value={formData.vin} onChange={handleInputChange} placeholder="99999999999999999" maxLength={17} />
            </div>
            <div className="form-group">
              <label htmlFor="yearMakeModel">Year, Make & Model</label>
              <input className="yearMakeModel" type="text" name="yearMakeModel" value={formData.yearMakeModel} onChange={handleChange} placeholder="Year, make & model" />
            </div>
            <div className="form-group">
              <label htmlFor="odometerReading">Odometer</label>
              <NumericFormat
                className="odometer"
                name="odometerReading"
                value={formData.odometerReading}
                onValueChange={(values) => {
                  const { value } = values;
                  setFormData((prevFormData) => ({
                    ...prevFormData,
                    odometerReading: value, // Salva o valor numérico sem formatação
                  }));
                }}
                thousandSeparator={","}
                fixedDecimalScale={true}
                placeholder="Odometer Reading"
                allowNegative={false} // Evita valores negativos, se necessário
              />
            </div>
          </form>
        </section>

        <section className="form-section">
          <h2>Damage / Repair Area</h2>
          <div className="car-container">
            <img src="/pngfind.com-car-top-view-png-750271 (1) 1.png" alt="Car" className="car-image" />
            <input type="checkbox" className="damage-checkbox" name="checkbox1" style={{ top: "15%", left: "12%" }} checked={checkboxes.checkbox1} onChange={handleCheckboxChange} />
            <input type="checkbox" className="damage-checkbox" name="checkbox2" style={{ top: "50%", left: "0%" }} checked={checkboxes.checkbox2} onChange={handleCheckboxChange} />
            <input type="checkbox" className="damage-checkbox" name="checkbox3" style={{ top: "85%", left: "12%" }} checked={checkboxes.checkbox3} onChange={handleCheckboxChange} />
            <input type="checkbox" className="damage-checkbox" name="checkbox4" style={{ top: "50%", left: "16%" }} checked={checkboxes.checkbox4} onChange={handleCheckboxChange} />
            <input type="checkbox" className="damage-checkbox" name="checkbox5" style={{ top: "10%", left: "35%" }} checked={checkboxes.checkbox5} onChange={handleCheckboxChange} />
            <input type="checkbox" className="damage-checkbox" name="checkbox6" style={{ top: "50%", left: "50%" }} checked={checkboxes.checkbox6} onChange={handleCheckboxChange} />
            <input type="checkbox" className="damage-checkbox" name="checkbox7" style={{ top: "85%", left: "35%" }} checked={checkboxes.checkbox7} onChange={handleCheckboxChange} />
            <input type="checkbox" className="damage-checkbox" name="checkbox8" style={{ top: "15%", left: "70%" }} checked={checkboxes.checkbox8} onChange={handleCheckboxChange} />
            <input type="checkbox" className="damage-checkbox" name="checkbox9" style={{ top: "85%", left: "70%" }} checked={checkboxes.checkbox9} onChange={handleCheckboxChange} />
            <input type="checkbox" className="damage-checkbox" name="checkbox10" style={{ top: "25%", left: "91%" }} checked={checkboxes.checkbox10} onChange={handleCheckboxChange} />
            <input type="checkbox" className="damage-checkbox" name="checkbox11" style={{ top: "50%", left: "94%" }} checked={checkboxes.checkbox11} onChange={handleCheckboxChange} />
            <input type="checkbox" className="damage-checkbox" name="checkbox12" style={{ top: "75%", left: "91%" }} checked={checkboxes.checkbox12} onChange={handleCheckboxChange} />
            <input type="checkbox" className="damage-checkbox" name="checkbox13" style={{ top: "50%", left: "100%" }} checked={checkboxes.checkbox13} onChange={handleCheckboxChange} />
          </div>

          <div className="checkbox-group">
            <label>
              <input type="checkbox" name="checkboxUnder" checked={checkboxes.checkboxUnder} onChange={handleCheckboxChange} /> Undercarriage
            </label>
            <label>
              <input type="checkbox" name="checkboxPdr" checked={checkboxes.checkboxPdr} onChange={handleCheckboxChange} /> PDR
            </label>
            <label>
              <input type="checkbox" name="checkboxFire" checked={checkboxes.checkboxFire} onChange={handleCheckboxChange} /> Fire Damage
            </label>
            <label>
              <input type="checkbox" name="checkboxOther" checked={checkboxes.checkboxOther} onChange={handleCheckboxChange} /> Other Damage
            </label>
            <label>
              <input type="checkbox" name="checkboxWater" checked={checkboxes.checkboxWater} onChange={handleCheckboxChange} /> Water Damage
            </label>
          </div>
        </section>

        <section className="form-section">
          <h2>Repair Estimate Review</h2>
          <form>
            <label htmlFor="laborHours">Labor Hours</label>
            <input
              className="laborHours"
              type="number"
              name="laborHours"
              value={formData?.laborHours}
              onChange={handleInputChange}
              placeholder="Enter labor hours"
              step="0.1" // Allow one decimal place
            />

            <div className="parentYesOrno">
              <div className="divForCheckboxes">
                <div className="structuralRow">
                  <div className="radio-group">
                    <label className="labelName">Structural Damage</label>
                    <div className="radio-inline">
                      <select className="optionsSD" name="structuralDamage" value={formData.structuralDamage} onChange={handleInputChange}>
                        <option value="None">None Reported</option>
                        <option value="Structural Damage Reported">Structural Damage Reported</option>
                        <option value="Body Pull Reported">Body Pull Reported</option>
                        <option value="Frame Damage Reported">Frame Damage Reported</option>
                        <option value="Structural Repair Reported">Structural Repair Reported</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="airbagRow">
                  <div className="radio-group">
                    {/*  */}
                    <label className="labelName">Airbag Deployment</label>
                    <select id="airbagDeployment" name="airbagDeployment" value={formData.airbagDeployment} onChange={handleInputChange} className="optionsSD">
                      <option value="no">None Reported</option>
                      <option value="yes">Airbag Deployment Reported</option>
                    </select>
                  </div>
                </div>

                <div className="priorRow">
                  <div className="radio-group">
                    <label className="labelName" htmlFor="priorLoss">
                      Prior Loss or UPD
                    </label>
                    <select id="priorLoss" name="priorLoss" value={formData.priorLoss} onChange={handleInputChange} className="optionsSD">
                      <option value="no">None reported</option>
                      <option value="yes">Prior Loss Reported</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="repairCost">Repair Cost</label>
              <NumericFormat
                className="laborHours"
                name="repairCost"
                value={formData.repairCost}
                onValueChange={(values) => {
                  const { value } = values;
                  handleInputChange({ target: { name: "repairCost", value } });
                }}
                thousandSeparator={","}
                decimalSeparator={"."}
                decimalScale={0}
                allowNegative={false}
                prefix="$"
                placeholder="$999"
              />
            </div>
          </form>
        </section>

        <section className="form-section">
          <h2>Third Party Valuation Data</h2>
          <form>
            <div className={`file-upload ${isDragging ? "dragging" : ""}`} onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop}>
              <input
                type="file"
                id="uploadBlackBook"
                name="uploadBlackBook"
                onChange={handleFileChange}
                accept="image/*" // Ensure only images are selectable in the file dialog
              />
              <label htmlFor="uploadBlackBook" className="file-upload-button">
                Select Files
              </label>
              {/* Display the file name if it exists */}
              {fileTag && <p className="file-name">Selected file: {fileTag}</p>}
              {/* Display error message if file is invalid */}
              {errorMessage && <p className="error-message">{errorMessage}</p>}
            </div>
          </form>
        </section>

        <section class="form-section">
          <h2>Market Value Analysis</h2>
          <form class="firstTable">
            <table className="tableContainer">
              <tr>
                <td class="div1">Initial Values</td>
                <td className="div1" style={{ paddingLeft: "80px" }}>
                  High
                </td>
                <td className="div1" style={{ paddingLeft: "80px" }}>
                  Low
                </td>
              </tr>
              <tr>
                <td class="div2">FMV</td>

                <td className="div2">
                  <NumericFormat
                    className="inputTable"
                    name="fmv_high"
                    value={formData.fmv_high || ""} // Bind to formData.fmv_high
                    onValueChange={(values) => {
                      const { value } = values; // Get unformatted value
                      handleInputChange({
                        target: { name: "fmv_high", value },
                      }); // Update form data
                    }}
                    thousandSeparator={true}
                    prefix="$"
                    placeholder="$99,999.00"
                    decimalScale={0}
                    allowNegative={false}
                    allowLeadingZeros={false}
                  />
                </td>

                <td className="div2">
                  <NumericFormat
                    className="inputTable"
                    name="fmv_low"
                    value={formData.fmv_low || ""} // Bind to formData.fmv_low
                    onValueChange={(values) => {
                      const { value } = values; // Get unformatted value
                      handleInputChange({ target: { name: "fmv_low", value } }); // Update form data
                    }}
                    thousandSeparator={true}
                    prefix="$"
                    placeholder="$99,999.00"
                    decimalScale={0}
                    allowNegative={false}
                    allowLeadingZeros={false}
                  />
                </td>
              </tr>

              <tr class="">
                <td class="div1" colspan="3">
                  Value Adjustments
                </td>
              </tr>
              <tr class="">
                <td className="div2">
                  <span className="letters">A -</span>
                  <select className="options" name="a_type" value={formData?.a_type} onChange={handleInputChange}>
                    <option value="N/A">N/A</option>
                    <option value="Equipment">Equipment</option>
                    <option value="Mileage">Mileage</option>
                  </select>

                  <select className="signSelectA" name="a_sign" value={formData?.a_sign || "+"} onChange={handleInputChange}>
                    <option value="+">+</option>
                    <option value="-">−</option>
                  </select>
                </td>

                <td className="div2">
                  <NumericFormat
                    className="inputTable"
                    name="a_high"
                    value={formData?.a_high === 0 ? "" : formData?.a_high}
                    onValueChange={(values) => {
                      const { value } = values;
                      handleInputChange({
                        target: { name: "a_high", value: Math.abs(value) },
                      });
                    }}
                    thousandSeparator=","
                    prefix="$"
                    placeholder="$99,999"
                    decimalScale={0}
                    allowNegative={false}
                    allowLeadingZeros={false}
                  />
                </td>

                <td className="div2">
                  <NumericFormat
                    className="inputTable"
                    name="a_low"
                    value={formData.a_low ? Math.round(formData.a_low) : ""}
                    onValueChange={(values) => {
                      const { value } = values;
                      handleInputChange({ target: { name: "a_low", value } });
                    }}
                    thousandSeparator=","
                    prefix="$"
                    placeholder="$99,999"
                    decimalScale={0}
                    allowNegative={false}
                    allowLeadingZeros={false}
                    readOnly
                  />
                </td>
              </tr>

              <tr>
                <td className="div2">
                  <span className="letters">B -</span>
                  <select className="options" name="b_type" value={formData?.b_type} onChange={handleInputChange}>
                    <option value="N/A">N/A</option>
                    <option value="UPD">UPD</option>
                    <option value="Equipment">Equipment</option>
                    <option value="Prior Loss">Prior Loss</option>
                    <option value="Market">Market</option>
                    <option value="Condition">Condition</option>
                    <option value="Date of Loss">Date of Loss</option>
                    <option value="Mileage">Mileage</option>
                    <option value="Other">Other</option>
                  </select>

                  <select className="signSelectB" name="b_sign" value={formData?.b_sign || "+"} onChange={handleInputChange}>
                    <option value="+">+</option>
                    <option value="-">−</option>
                  </select>
                </td>

                <td className="div2">
                  <NumericFormat
                    className="inputTable"
                    name="b_high"
                    value={formData?.b_high === 0 ? "" : formData?.b_high} // Exibe valor vazio se for 0
                    onValueChange={(values) => {
                      const { value } = values; // Obtém o valor numérico não formatado
                      handleInputChange({
                        target: { name: "b_high", value: Math.abs(value) },
                      }); // Usa handleInputChange para atualizar o valor
                    }}
                    thousandSeparator=","
                    prefix="$"
                    placeholder="$99,999"
                    decimalScale={0}
                    allowNegative={false}
                    allowLeadingZeros={false}
                  />
                </td>

                <td className="div2">
                  <NumericFormat
                    className="inputTable"
                    name="b_low"
                    value={formData.b_low ? Math.round(formData.b_low) : ""} // Exibe valor arredondado ou vazio se for 0
                    onValueChange={(values) => {
                      const { value } = values; // Obtém o valor numérico não formatado
                      handleInputChange({ target: { name: "b_low", value } }); // Usa handleInputChange para atualizar o valor
                    }}
                    thousandSeparator="," // Adiciona o separador de milhar
                    prefix="$" // Adiciona o símbolo de dólar
                    placeholder="$99,999" // Placeholder formatado
                    decimalScale={0} // Garante que não haja casas decimais
                    allowNegative={false} // Não permite valores negativos
                    allowLeadingZeros={false} // Não permite zeros à esquerda
                  />
                </td>
              </tr>

              <tr>
                <td className="div2">
                  <span className="letters">C -</span>
                  <select className="options" name="c_type" value={formData?.c_type} onChange={handleInputChange}>
                    <option value="N/A">N/A</option>
                    <option value="UPD">UPD</option>
                    <option value="Equipment">Equipment</option>
                    <option value="Prior Loss">Prior Loss</option>
                    <option value="Market">Market</option>
                    <option value="Condition">Condition</option>
                    <option value="Date of Loss">Date of Loss</option>
                    <option value="Mileage">Mileage</option>
                    <option value="Other">Other</option>
                  </select>

                  <select className="signSelectC" name="c_sign" value={formData?.c_sign || "+"} onChange={handleInputChange}>
                    <option value="+">+</option>
                    <option value="-">−</option>
                  </select>
                </td>

                <td className="div2">
                  <NumericFormat
                    className="inputTable"
                    name="c_high"
                    value={formData?.c_high === 0 ? "" : formData?.c_high} // Exibe valor vazio se for 0
                    onValueChange={(values) => {
                      const { value } = values; // Obtém o valor numérico não formatado
                      handleInputChange({
                        target: { name: "c_high", value: Math.abs(value) },
                      });
                    }}
                    thousandSeparator="," // Adiciona o separador de milhar
                    prefix="$" // Adiciona o símbolo de dólar
                    placeholder="$99,999" // Placeholder formatado
                    decimalScale={0} // Não permite casas decimais
                    allowNegative={false} // Não permite valores negativos
                    allowLeadingZeros={false} // Não permite zeros à esquerda
                  />
                </td>

                <td className="div2">
                  <NumericFormat
                    className="inputTable"
                    name="c_low"
                    value={formData.c_low ? Math.round(formData.c_low) : ""} // Exibe o valor arredondado ou vazio
                    onValueChange={(values) => {
                      const { value } = values; // Obtém o valor numérico não formatado
                      handleInputChange({ target: { name: "c_low", value } }); // Usa handleInputChange para atualizar o valor
                    }}
                    thousandSeparator="," // Adiciona o separador de milhar
                    prefix="$" // Adiciona o símbolo de dólar
                    placeholder="$99,999" // Placeholder formatado
                    decimalScale={0} // Não permite casas decimais
                    allowNegative={false} // Não permite valores negativos
                    allowLeadingZeros={false} // Não permite zeros à esquerda
                  />
                </td>
              </tr>

              <tr class="">
                <td class="div1" colspan="3">
                  Final Values
                </td>
              </tr>
              <tr>
                <td class="div2"></td>

                <td className="div2">
                  <NumericFormat
                    className="inputTable"
                    name="final_high"
                    value={formData.final_high ? parseFloat(formData.final_high).toFixed(0) : ""} // Formata o valor com duas casas decimais
                    thousandSeparator="," // Adiciona o separador de milhar
                    prefix="$" // Adiciona o símbolo de dólar
                    placeholder="$99,999" // Placeholder formatado
                    decimalScale={0} // Exibe sempre duas casas decimais
                    readOnly // Impede a edição direta do campo
                  />
                </td>

                <td className="div2">
                  <NumericFormat
                    className="inputTable"
                    name="final_low"
                    value={formData.final_low ? parseFloat(formData.final_low).toFixed(0) : ""} // Formata o valor com duas casas decimais
                    thousandSeparator="," // Adiciona o separador de milhar
                    prefix="$" // Adiciona o símbolo de dólar
                    placeholder="$99,999" // Placeholder formatado
                    decimalScale={0} // Exibe sempre duas casas decimais
                    readOnly // Impede a edição direta do campo
                  />
                </td>
              </tr>
              <tr class="">
                <td class="div1" colspan="3">
                  Maximum Loss Range
                </td>
              </tr>
              <tr>
                <td class="div2"></td>
                <td class="div2"></td>

                <td className="div2">
                  <NumericFormat
                    className="inputTable"
                    name="final_high"
                    value={formData.max_loss ? parseFloat(formData.max_loss).toFixed(0) : ""} // Formata o valor com duas casas decimais
                    thousandSeparator="," // Adiciona o separador de milhar
                    prefix="$" // Adiciona o símbolo de dólar
                    placeholder="$99,999" // Placeholder formatado
                    decimalScale={0} // Exibe sempre duas casas decimais
                    readOnly // Impede a edição direta do campo
                  />
                </td>
              </tr>
              <tr class="">
                <td class="div1" colspan="3">
                  Total Loss Threshold
                </td>
              </tr>
              <tr>
                <td class="div2"></td>
                <td class="div2"></td>

                <td className="div2">
                  <NumericFormat
                    className="inputTable"
                    name="final_low"
                    value={formData.loss_thresold ? parseFloat(formData.loss_thresold).toFixed(0) : ""} // No decimals
                    thousandSeparator="," // Adds commas for thousands
                    prefix="$" // Adds dollar symbol
                    placeholder="$99,999" // Shows formatted placeholder
                    decimalScale={0} // Ensures no decimal places
                    readOnly // Sets field as read-only
                  />
                </td>
              </tr>
            </table>
          </form>
        </section>

        <section className="form-section" style={{ marginBottom: "100px" }}>
          <h2>Damage Analysis</h2>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="damageValue">Damage to Value</label>
              <input className="damageValue" type="text" id="damageValue" placeholder="99%" value={formData.damage_to_value ? formData.damage_to_value + "%" : ""} readOnly />
            </div>
            <div className="form-group">
              <label htmlFor="damageScore">Damage Severity Score & Grade</label>
              <input className="damageScore" type="text" id="damageScore" placeholder="99" value={formData.damageScore || ""} onChange={(e) => handleDamageScoreChange(e.target.value)} />
              <input className="grade" type="text" id="grade" placeholder="9" value={formData.grade || ""} readOnly />
            </div>
          </form>
        </section>

        <section className="form-section">
          <h2>Vehicle Values Chart</h2>
          <div className="chart-wrapper">
            <MemoizedChartComponent
              finalHighValue={memoizedFormData.finalHighValue}
              postRepairFMV={memoizedFormData.postRepairFMV}
              diminishedValue={memoizedFormData.diminishedValue}
              setChartImage={setChartImage}
              textColor="#000"
            />
          </div>
        </section>

        <section className="form-section">
          <h2>Diminished Value Summary</h2>
          <form>
            <table className="finalTableContainer">
              <tbody>
                <tr className="firstDmsDivContainer">
                  <td className="firstDmsDiv">Pre-Loss FMV</td>
                </tr>
                <tr className="firstDmsDivContainer">
                  <td className="secondDmsDivContainer">
                    <input
                      className="finalDisplay"
                      type="text"
                      name="PreLossFMV"
                      placeholder="$99,999"
                      value={formData.PreLossFMV ? `$${Number(formData.PreLossFMV).toLocaleString("en-US")}` : ""}
                      readOnly
                    />
                  </td>
                </tr>

                <tr className="firstDmsDivContainer">
                  <td className="firstDmsDiv">Maximum DV</td>
                </tr>
                <tr className="firstDmsDivContainer">
                  <td className="secondDmsDivContainer">
                    <input
                      className="finalDisplay"
                      type="text"
                      name="final_high"
                      placeholder="$99,999"
                      value={formData.max_loss ? `$${Math.round(Number(formData.max_loss)).toLocaleString("en-US")}` : ""}
                      readOnly
                    />
                  </td>
                </tr>

                <tr className="firstDmsDivContainer">
                  <td className="firstDmsDiv">Inherent DV</td>
                </tr>
                <tr className="firstDmsDivContainer">
                  <td className="secondDmsDivContainer">
                    <input
                      className="finalDisplay"
                      type="text"
                      name="inherentDV"
                      placeholder="$99,999"
                      value={formData.inherentDV ? `$${Math.round(Number(formData.inherentDV)).toLocaleString("en-US")}` : ""}
                      readOnly
                    />
                  </td>
                </tr>

                <tr className="firstDmsDivContainer">
                  <td className="firstDmsDiv">Repair Related DV</td>
                </tr>
                <tr className="firstDmsDivContainer">
                  <td className="secondDmsDivContainer">
                    <input className="finalDisplay" type="text" name="final_high" placeholder="$99,999" value="0" readOnly />
                  </td>
                </tr>

                <tr className="firstDmsDivContainer">
                  <td className="firstDmsDiv">Post Repair FMV</td>
                </tr>
                <tr className="firstDmsDivContainer">
                  <td className="secondDmsDivContainer">
                    <input
                      className="finalDisplay"
                      type="text"
                      name="postRepairFMV"
                      placeholder="$99,999"
                      value={formData.postRepairFMV ? `$${Math.round(Number(formData.postRepairFMV)).toLocaleString("en-US")}` : ""}
                      readOnly
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </form>
        </section>
      </div>
      <footer>
        <h2>Compliance Statements</h2>

        <div className="personal">
          <label class="inspection-label">A personal inspection of the properties that are the subject of this report was performed.</label>
          <label class="checkbox-label">
            <input type="checkbox" class="custom-checkbox" name="checkboxYes" checked={checkboxes.checkboxYes} onChange={handleCheckboxChange} />
            <span class="checkbox-text">
              <div className="yesText">Yes</div>
            </span>
          </label>
        </div>

        {/*<input type="checkbox" name="checkboxOther" checked={checkboxes.checkboxOther} onChange={handleCheckboxChange}/> Other Damage*/}

        {showPreview && pdfBlob && (
          <div className="pdf-preview-modal">
            <div className="pdf-preview-content">
              <div className="vin-name">
                VIN: {formData.vin ? formData.vin : "N/A"}
                <span className="preview-text">(This is a preview. Save the form to generate the final PDF version)</span>
              </div>
              <iframe src={pdfUrl} width="100%" height="900px" title="PDF Preview" />
              <div className="grayContainer"></div>
              <div className="button-group">
                <button className="open-button" onClick={() => window.open(URL.createObjectURL(pdfBlob), "_blank")}>
                  Open in a new tab
                </button>
                {/*
                    <button
                      className="copy-link-button"
                      onClick={() => {
                        const link = URL.createObjectURL(pdfBlob);
                        navigator.clipboard.writeText(link);
                        alert("Link copied to clipboard!");
                      }}
                    >
                      Copy link
                    </button>
                  */}
                <button
                  className="download-button"
                  onClick={() => {
                    const link = URL.createObjectURL(pdfBlob);
                    const a = document.createElement("a");
                    a.href = link;
                    a.download = formData.vin ? `${formData.vin}.pdf` : "AppraisalReport.pdf";
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                  }}
                >
                  Download locally
                </button>
                <button className="close-button" onClick={closePreview}>
                  X
                </button>
              </div>
            </div>
          </div>
        )}
      </footer>
      <div className="dv-total">
        <div className="total-dv-2000-container">
          <p>
            <strong>Total DV:</strong> {formData.diminishedValue ? `$${formData.diminishedValue}` : "0"}
          </p>
        </div>
      </div>
    </div>
  );
};

export default AppraisalReportForm;
